import type { BuildHubTypeEnum, BuildHubRoleEnum } from 'generated/graphql';
import type { SavedBudgetRange } from '@modules/build-hub-creator/api/types';
import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';

interface DesignBoardCollaborator {
    name: string;
    email: string;
    image: string | null;
}

export interface BuildHubCollaborator {
    id: string;
    name: string;
    lastInviteSent?: string;
    lastInviteLink?: string;
    email: string;
    image: string | null;
    teamDisplayImage?: string | null;
    role: BuildHubRoleEnum;
    onBoarded: boolean;
    memberID?: string;
    invitedByMemberID?: number;
    caption?: string;
    customCompanyName?: string;
    sortOrder: number;
    teamMember?: {
        id: string;
        name: string;
        jobTitle?: string;
        memberImage?: {
            id: string;
            fileName?: string;
            teamMemberDisplay?: string;
        };
        professional?: {
            id: string;
            title: string;
            urlSegment: string;
        };
    };
}

export interface BuildHubUserPermission {
    canEdit: boolean;
    canDelete: boolean;
    canEditCollaborator: boolean;
}

/**
 * Lightweight assignee representation for stages.
 * Contains minimal info needed to display the assigned collaborator.
 */
interface StageAssignee {
    id: string;
    name: string;
    email: string;
    image: string | null;
}

interface BuildHubStage {
    id: string;
    title: string;
    urlSegment: string;
    sortOrder: number;
    isActive: boolean;
    isCompleted: boolean;
    startDate: string | null;
    completionDate: string | null;
    progress: number;
    isDisabled: boolean;
    /** ID of the assigned collaborator (null if unassigned) */
    assigneeId: string | null;
    /** The assigned collaborator details (null if unassigned) */
    assignee: StageAssignee | null;
}

interface BuildHubMilestone {
    id: string;
    title: string;
    urlSegment: string;
    sortOrder: number;
    isCompleted: boolean;
    date: string | null;
    isDisabled: boolean;
}

interface BuildHubTimelineSettings {
    id: string;
    viewMode: string;
    showGridLines: boolean;
    showMinorGridLines: boolean;
    workingDays: number[];
    showNonWorkingDays: boolean;
    highlightNonWorkingDays: boolean;
}

export interface BuildHub {
    id: string;
    title: string;
    urlSegment: string;
    link: string; // app/project/build-hub-id (base 64 hash of id)
    type: BuildHubTypeEnum;
    scope: string; // Display label (e.g., "New Home Build", "Home Renovation")
    status: string | null; // Title of the active stage
    description: string | null;
    image: string | null;
    thumbnail: string | null;
    location: string;
    locationData?: DetailedPlace | null;
    budget: SavedBudgetRange;
    collaborators: BuildHubCollaborator[];
    availableStages: BuildHubStage[];
    milestones?: BuildHubMilestone[];
    timelineSettings?: BuildHubTimelineSettings | null;
    created: string;
    createdBy?: string;
    ownerId: number;
    owner: {
        id: number;
        name: string;
        email: string;
        image?: string | null;
    };
    lastEdited: string;
    lastActivity: string | null;
    startDate: string | null;
    endDate: string | null; // maybe this should be a proposed end date? might be cool to have a calcuated end date based on the scope and timeline
    userPermissions: BuildHubUserPermission;
    currentUserRole?: BuildHubRoleEnum;
    unreadCount?: number;
}

export interface BuildHubDesignBoard {
    id: number;
    sourceId?: number;
    title: string;
    images: BoardImage[];
    lastEdited: string;
    boardMembers: DesignBoardCollaborator[];
    createdBy: string;
    link: string;
    isPublic: boolean;
    description: string;
    owner: {
        id: string;
        name: string;
        email: string;
        memberImage?: {
            id: string;
            fileName?: string;
            logo?: string;
            largeAvatar?: string;
        } | null;
    };
    generalAccessMode: string;
    generalAccessRole: string;
    futureTeamMemberRole: string;
    unreadCount?: number;
}

export interface BoardImage {
    src: string;
    backgroundColor?: string;
}

export interface BuildHubDocumentFolder {
    id: string;
    title: string;
    link: string;
    lastUpdated: string;
    documentMembers: BuildHubCollaborator[];
    fileCount: number;
    files: BuildHubDocument[];
}

export interface BuildHubDocument {
    id: string;
    fileName: string;
    fileType: string;
    fileSize: number;
    dateTimeCreated: string;
    dateTimeModified: string;
    lastOpened?: string;
    previewUrl?: string;
    downloadUrl: string;
}

/**
 * Access modes for BuildHub documents.
 * Determines who can access the document.
 */
export const DOCUMENT_ACCESS_MODE = {
    /** Inherit permissions from parent document or BuildHub */
    Inherit: 'inherit',
    /** Only specific collaborators can access */
    Restricted: 'restricted',
    /** Anyone with the link can access */
    Link: 'link',
} as const;

export const DOCUMENT_ACCESS_MODE_VALUES = [
    DOCUMENT_ACCESS_MODE.Inherit,
    DOCUMENT_ACCESS_MODE.Restricted,
    DOCUMENT_ACCESS_MODE.Link,
] as const;

export type DocumentAccessMode = (typeof DOCUMENT_ACCESS_MODE_VALUES)[number];

/**
 * Roles for BuildHub document collaborators.
 * Determines what actions a collaborator can perform.
 */
export const DOCUMENT_ROLE = {
    /** Can only view the document */
    Viewer: 'Viewer',
    /** Can view and comment on the document */
    Commenter: 'Commenter',
    /** Can view, comment, and edit the document */
    Editor: 'Editor',
} as const;

export const DOCUMENT_ROLE_VALUES = [
    DOCUMENT_ROLE.Viewer,
    DOCUMENT_ROLE.Commenter,
    DOCUMENT_ROLE.Editor,
] as const;

export type DocumentRole = (typeof DOCUMENT_ROLE_VALUES)[number];

/**
 * A collaborator with specific access to a BuildHub document.
 */
export interface DocumentCollaborator {
    id?: string;
    email: string;
    name: string;
    image?: string | null;
    role?: DocumentRole;
    memberID?: string;
    sortOrder?: number;
    /** Whether this is a pending invite (user hasn't accepted yet) */
    isPending?: boolean;
}

/**
 * Lightweight representation of a collaborator selection for document access.
 */
export interface DocumentCollaboratorSelection {
    email: string;
    role: DocumentRole;
    name?: string;
}
